import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";

const WHATSAPP_LINK = "https://wa.me/573107544509";

// Hide Emergent badge
const hideEmergentStyle = `
  .emergent-badge, 
  [class*="emergent"], 
  [data-testid*="emergent"],
  div[style*="Made with"],
  a[href*="emergent"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
`;

const BrochurePage = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      {/* Hide Emergent Badge */}
      <style>{hideEmergentStyle}</style>
      
      <div className="bg-[#0D0906] text-[#FBF7F4] overflow-x-hidden" data-testid="brochure-premium">
        
        {/* ==================== SLIDE 1: PORTADA ==================== */}
        <section className="min-h-screen relative flex items-center justify-center px-6 py-20">
          {/* Background effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#C88037]/5 blur-[150px]" />
            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#0D0906] to-transparent" />
          </div>
          
          {/* Gold corner ornaments */}
          <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-[#C88037]/40" />
          <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-[#C88037]/40" />
          <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-[#C88037]/40" />
          <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-[#C88037]/40" />

          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 1 }}
            className="relative z-10 text-center max-w-lg mx-auto"
          >
            {/* Logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/3ooof9nm_2-removebg-preview.png"
              alt="Urban Brownies"
              className="h-20 mx-auto mb-10 brightness-0 invert"
            />
            
            {/* Decorative line */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#C88037]" />
              <span className="text-[#C88037] text-xs tracking-[0.4em] uppercase">Día de la Madre</span>
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#C88037]" />
            </div>

            {/* Main text */}
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-relaxed mb-8">
              Hay un momento en la vida<br />de toda mamá.
            </h1>
            
            <p className="text-xl sm:text-2xl leading-relaxed text-[#D4A574] mb-8">
              Es cuando abre un regalo y se da cuenta<br />
              de que es <span className="text-[#C88037] font-bold italic text-3xl">LA ELEGIDA.</span>
            </p>

            {/* Ornament */}
            <div className="text-[#C88037] text-4xl mb-8">✦</div>

            <p className="text-lg sm:text-xl text-[#FBF7F4]/80 leading-relaxed mb-4">
              Aquí, ese momento tiene sabor a chocolate artesanal<br />
              y aroma a cuidado en cada detalle.
            </p>

            <p className="text-lg text-[#D4A574] mb-4">
              Chocolate puro. Dulce de leche. Caramelo.
            </p>
            
            <p className="text-xl text-[#C88037] font-medium mb-12">
              Y 4 sorpresas más.
            </p>

            {/* Badge */}
            <div className="inline-block border-2 border-[#C88037] px-8 py-4 rounded-none">
              <span className="font-serif text-2xl sm:text-3xl text-[#C88037]">
                7 formas de hacerla sentir ELEGIDA
              </span>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 2: EL DETALLITO DE MAMÁ ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#FBF7F4] text-[#1A1512]">
          {/* Gold accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            {/* Number badge */}
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">01</span>
            </div>

            {/* Image with frame */}
            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/4gwfekdn_CuadradoCorazon%20%283%29.png"
                alt="El Detallito de Mamá"
                className="w-full aspect-square object-cover"
              />
            </div>

            {/* Title */}
            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#2D1810]">
              El Detallito de Mamá
            </h2>

            {/* Ornament */}
            <div className="text-[#C88037] text-3xl text-center mb-8">❧</div>

            {/* Copy */}
            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p>
                Los brownies cuadrados con corazón son lo que<br />
                pasaría si el cariño tuviera forma, sabor y <span className="text-[#C88037] font-bold">AROMA.</span>
              </p>
              
              <p className="text-[#5E4B3C] italic">
                Chocolate que respira en cada bocado.
              </p>
              
              <p className="text-2xl font-medium text-[#2D1810]">
                Pequeño. Poderoso. Imposible de olvidar.
              </p>
              
              <p className="text-[#5E4B3C]">
                Elige cuántos quieres — porque a veces<br />
                <span className="text-[#C88037] font-medium">lo más sencillo es lo que más llega al corazón.</span>
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 3: URBAN HEARTS ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">02</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/84wo6012_CorazonChocolate%20%283%29.png"
                alt="Urban Hearts"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#FBF7F4]">
              Urban Hearts
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">♥</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl font-medium text-[#D4A574] italic">
                Irresistibles por dentro y por fuera<br />
                — igual que ella.
              </p>
              
              <p className="text-[#FBF7F4]/80">
                Por dentro: lo más suave, lo más especial.<br />
                Por fuera: belleza sin compromisos.
              </p>
              
              <p className="text-[#C88037] text-2xl font-medium">
                El regalo que no necesita tarjeta<br />para decir todo.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 4: CAJITA DE SECRETOS ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#FBF7F4] text-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">03</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/4rbf8a63_CajaXMensaje%20%283%29.png"
                alt="Cajita de Secretos"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#2D1810]">
              Cajita de Secretos
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">✉</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl text-[#2D1810]">
                ¿Qué pasaría si mamá abriera una caja de brownies<br />
                y dentro encontrara las palabras<br />
                <span className="text-[#C88037] font-bold text-3xl">que NUNCA le dijiste?</span>
              </p>
              
              <div className="py-6 space-y-2 text-[#5E4B3C]">
                <p>3 brownies recién horneados.</p>
                <p>Tu mensaje personalizado.</p>
                <p>Salsa a tu elección (arequipe o chocolate).</p>
              </div>
              
              <p className="text-[#C88037] text-xl font-medium italic">
                Porque hay cosas que solo se dicen así.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 5: HECHO SOLO PARA ELLA ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">04</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/4g8opu37_CajaPersonalizada%20%282%29.png"
                alt="Hecho Solo Para Ella"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#FBF7F4]">
              Hecho Solo Para Ella
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">✧</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl text-[#D4A574]">
                ¿Sabías que los regalos más memorables<br />
                <span className="text-[#C88037] font-bold">NO</span> son los más caros, sino los <span className="text-[#C88037] font-bold">EXACTOS?</span>
              </p>
              
              <p className="text-[#FBF7F4]/80">
                Cuéntanos qué quieres.<br />
                Nosotros lo hacemos realidad en 24-48h.
              </p>
              
              <p className="text-[#FBF7F4]">
                Una caja de 3 brownies diseñada<br />
                <span className="italic">exactamente como tú la imaginas.</span>
              </p>
              
              <p className="text-[#C88037] text-xl font-medium">
                Este regalo no existe en ningún otro lado.<br />
                Y eso es precisamente lo que la hará llorar.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 6: CAJA FELIZ DÍA MAMÁ ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#FBF7F4] text-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">05</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/snlhelmr_CajaFelizDia%20%284%29.png"
                alt="Caja Feliz Día Mamá"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#2D1810]">
              Caja Feliz Día Mamá
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">❀</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-[#2D1810]">
                Cuatro muffin brownies decorados,<br />
                listos para hacerla sonreír en su día.
              </p>
              
              <p className="text-[#5E4B3C]">
                Una caja grande, llena de sabor y cariño,<br />
                perfecta para celebrarla como se merece.
              </p>
              
              <p className="text-[#C88037] text-xl font-medium italic">
                Porque a veces la generosidad<br />
                habla más que mil discursos.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 7: EL DOMO DE LA REINA ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">06</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/8xxrr515_DomoPersonal%20%282%29.png"
                alt="El Domo de la Reina"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#FBF7F4]">
              El Domo de la Reina
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">♛</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl text-[#FBF7F4]">
                Una torta personal en domo.
              </p>
              
              <p className="text-[#D4A574] italic">
                Traducción:<br />
                <span className="text-[#C88037] text-2xl font-medium">Tú mereces tu propio universo.</span>
              </p>
              
              <p className="text-[#FBF7F4]/80">
                Para la reina de tu vida, solo lo mejor.<br />
                Con tu mensaje personalizado en la torta.
              </p>
              
              <p className="text-[#C88037] text-xl font-medium">
                Porque algunos regalos no son regalos.<br />
                <span className="text-2xl">Son coronaciones.</span>
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 8: LA TORTA DE MAMÁ ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-[#FBF7F4] text-[#1A1512]">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C88037] to-transparent" />
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block w-12 h-12 border-2 border-[#C88037] rounded-full leading-[44px] text-[#C88037] font-serif text-xl">07</span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-3 border border-[#C88037]/30" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/wk5qnsw1_Torta1_4%20%282%29.png"
                alt="La Torta de Mamá"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-4xl sm:text-5xl text-center mb-8 text-[#2D1810]">
              La Torta de Mamá
            </h2>

            <div className="text-[#C88037] text-3xl text-center mb-8">♥</div>

            <div className="text-center space-y-6 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl text-[#2D1810]">
                Una torta de corazón.
              </p>
              
              <p className="text-[#5E4B3C]">
                Compártela con ella o regálasela entera<br />
                — de cualquier forma,<br />
                el mensaje es el mismo:
              </p>
              
              <p className="text-[#C88037] text-4xl font-serif italic">
                Te quiero con todo.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ==================== SLIDE 9: SWEET MAMÁ ==================== */}
        <section className="min-h-screen relative px-6 py-20 bg-gradient-to-b from-[#1A1512] via-[#2D1810] to-[#1A1512]">
          {/* Glow effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#C88037]/10 blur-[120px]" />
          </div>
          
          {/* Gold corner ornaments */}
          <div className="absolute top-8 left-8 w-20 h-20 border-l-2 border-t-2 border-[#C88037]/60" />
          <div className="absolute top-8 right-8 w-20 h-20 border-r-2 border-t-2 border-[#C88037]/60" />
          <div className="absolute bottom-8 left-8 w-20 h-20 border-l-2 border-b-2 border-[#C88037]/60" />
          <div className="absolute bottom-8 right-8 w-20 h-20 border-r-2 border-b-2 border-[#C88037]/60" />

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.8 }}
            className="relative z-10 max-w-lg mx-auto"
          >
            {/* Limited badge */}
            <div className="text-center mb-6">
              <span className="inline-block bg-[#C88037] text-[#0D0906] text-sm font-bold tracking-[0.3em] uppercase px-6 py-3">
                EDICIÓN LIMITADA
              </span>
            </div>

            <div className="relative mb-10">
              <div className="absolute -inset-4 border-2 border-[#C88037]/40" />
              <div className="absolute -inset-6 border border-[#C88037]/20" />
              <img
                src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/1bjwy2x1_SweetMama%20%282%29.png"
                alt="Sweet Mamá"
                className="w-full aspect-square object-cover"
              />
            </div>

            <h2 className="font-serif text-5xl sm:text-6xl text-center mb-4 text-[#FBF7F4]">
              Sweet Mamá
            </h2>
            
            <p className="text-center text-[#D4A574] text-xl italic mb-10">
              Solo para la mamá más especial.
            </p>

            <div className="text-[#C88037] text-3xl text-center mb-10">✦ ✦ ✦</div>

            <div className="space-y-4 text-lg sm:text-xl mb-10">
              <p className="flex items-start gap-4 text-[#FBF7F4]">
                <span className="text-[#C88037] text-2xl">+</span>
                <span>5 Urban Pops (chocolate, caramelo, pistacho)</span>
              </p>
              <p className="flex items-start gap-4 text-[#FBF7F4]">
                <span className="text-[#C88037] text-2xl">+</span>
                <span>Urban Cup personalizado (arequipe o chocolate)</span>
              </p>
              <p className="flex items-start gap-4 text-[#FBF7F4]">
                <span className="text-[#C88037] text-2xl">+</span>
                <span>Tu mensaje personalizado</span>
              </p>
              <p className="flex items-start gap-4 text-[#C88037] font-medium">
                <span className="text-2xl">+</span>
                <span>LLAVERO DE CORAZÓN — Nuestro regalo para ti</span>
              </p>
            </div>

            <div className="bg-[#C88037]/10 border border-[#C88037]/30 p-6 text-center mb-10">
              <p className="text-[#C88037] text-lg">
                Los primeros 5 que la pidan,<br />
                <span className="text-2xl font-bold">el llavero es NUESTRO REGALO.</span>
              </p>
            </div>

            <p className="text-center text-2xl text-[#FBF7F4] mb-10">
              Porque tu mamá es <span className="text-[#C88037] font-bold italic text-3xl">LA ELEGIDA.</span>
            </p>

            {/* CTA */}
            <motion.a
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              href={`${WHATSAPP_LINK}?text=${encodeURIComponent("¡Hola! Quiero pedir Sweet Mamá — Edición Limitada 🌸")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-4 w-full bg-[#C88037] text-[#0D0906] py-5 text-xl font-bold tracking-wide hover:bg-[#E5984A] transition-colors"
              data-testid="cta-main"
            >
              <MessageCircle className="w-6 h-6" strokeWidth={2} />
              PEDIR AHORA
            </motion.a>
          </motion.div>
        </section>

        {/* ==================== FOOTER ==================== */}
        <section className="px-6 py-20 bg-[#0D0906] text-center">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-px bg-[#C88037]" />
              <span className="text-[#C88037]">✦</span>
              <div className="w-16 h-px bg-[#C88037]" />
            </div>

            <img
              src="https://customer-assets.emergentagent.com/job_brownie-quiz-store/artifacts/3ooof9nm_2-removebg-preview.png"
              alt="Urban Brownies"
              className="h-16 mx-auto mb-8 brightness-0 invert opacity-60"
            />

            <p className="text-[#D4A574] text-lg mb-8">
              Haz tu pedido por WhatsApp
            </p>

            <motion.a
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              href={`${WHATSAPP_LINK}?text=${encodeURIComponent("¡Hola! Quiero hacer un pedido para el Día de la Madre 🌸🍫")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-4 text-xl font-semibold hover:bg-[#20BD5A] transition-colors"
              data-testid="cta-footer"
            >
              <MessageCircle className="w-6 h-6" strokeWidth={2} />
              +57 310 754 4509
            </motion.a>

            <p className="text-[#FBF7F4]/40 text-sm mt-12">
              @urbanbrownies1
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default BrochurePage;
